<template>
  <div
    ref="hiddenContainer"
    :class="classNames"
    class="hidden-container"
  >
    <div class="hidden-container__body">
      <div class="list-title">
        <strong>{{ selectedYear }}</strong> Ranking {{ statefulFilters.Inwoners.label }}
      </div>
      <div class="hidden-container__scroll-x">
        <slot />
      </div>
    </div>
    <button
      class="hidden-container__button"

      @click="toggleContainer"
    >
      {{ buttonText }}
    </button>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import getEventPath from '@/helpers/mouse-event';

export default {
  name: 'HiddenElementsContainer',

  props: {
    /**
     * To which side is the container positioned<br />
     * `left` or `right`
     */
    position: {
      type: String,
      required: false,
      default: 'right',
      validator: (value) => ['right', 'left'].includes(value),
    },

    /**
     * Hide the container after amount of milliseconds
     */
    hideAfterMilliseconds: {
      type: Number,
      required: false,
      default: 0,
    },

    /**
     * Should the container remain open when visiting the page
     */
    defaultOpen: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Hide container on click outside
     */
    clickOutside: {
      type: Boolean,
      required: false,
      default: true,
    },

    /**
     * The text in the toggle button to open the container
     */
    buttonText: {
      type: String,
      required: false,
      default: 'Bekijk de content',
    },
  },

  data() {
    return {
      isHidden: !this.defaultOpen,
    };
  },

  computed: {
    ...mapState({
      selectedItem: (state) => state.list.selected,
      selectedYear: (state) => state.rawData.year,
    }),

    ...mapGetters({
      statefulFilters: 'filter/filters',
    }),

    currentFilterLabel() {
      return this.statefulFilters?.Inwoners?.label;
    },

    classNames() {
      const classNames = [
        `--${this.position}`,
      ];

      if (this.isHidden) {
        classNames.push('--hide');
      }

      return classNames;
    },
  },

  watch: {
    selectedItem(value) {
      if (value !== null && !this.isHidden) {
        this.toggleContainer();
      }
    },
  },

  created() {
    // Immediately hide the container when we don't want a delay
    if (!this.defaultOpen && !this.isHidden && this.hideAfterMilliseconds === 0) {
      this.toggleContainer();
    }
  },

  mounted() {
    // Hide the container after a delay
    if (this.defaultOpen && this.hideAfterMilliseconds !== 0) {
      setTimeout(() => {
        this.toggleContainer();
      }, this.hideAfterMilliseconds);
    }

    if (this.clickOutside) {
      document.body.addEventListener('click', this.onClickOutside);
    }
  },

  methods: {
    toggleContainer() {
      this.isHidden = !this.isHidden;

      this.$emit('toggle', this.isHidden);
    },

    onClickOutside(event) {
      const path = getEventPath(event);
      const component = this.$refs.hiddenContainer;
      const filter = document.querySelector('.filter-bar__facets');

      if (
        component === event.target
        || path.indexOf(component) > -1
        || path.indexOf(filter) > -1
      ) {
        return;
      }

      if (!this.isHidden) {
        this.toggleContainer();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .hidden-container {
    background-color: $color-white;
    box-shadow: 0 4px 10px rgba($color-silver, .5);
    height: 100%;
    position: relative;
    z-index: get-z-index();

    &__body {
      height: 100%;
      overflow: hidden;
      overflow-y: auto;
      padding: $global-spacing-unit $global-spacing-unit $global-spacing-unit-quadruple;

      .filter-open & {
        padding-bottom: $global-spacing-unit-quadruple + 53;

        @include mq($until: tablet) {
          padding-bottom: $global-spacing-unit;
        }
      }

      @include mq($until: tablet) {
        padding: $global-spacing-unit $global-spacing-unit;
      }
    }

    &__scroll-x {
      overflow: hidden;

      @include mq($until: tablet) {
        overflow-x: auto;
      }
    }

    &__button {
      @include font-size(theta);

      animation: none;
      background-color: $color-tundora;
      border: 0;
      border-top-left-radius: $global-radius;
      border-top-right-radius: $global-radius;
      color: $color-white;
      cursor: pointer;
      font-family: $font-family-base;
      font-weight: normal;
      margin: 0;
      outline: 0;
      padding: 8px 20px;
      position: absolute;
      text-transform: uppercase;
      top: 125px;
      white-space: nowrap;

      @include mq($until: tablet) {
        @include font-size(milli);
      }
    }

    &.\--right {
      .hidden-container__button {
        left: 0;
        transform: translateX(-100%) translateY(-100%) rotate(-90deg);
        transform-origin: right bottom;
      }

      @include mq($until: tablet) {
        .hidden-container__button {
          left: 100%;
          top: 280px;
          transform: translateY(-100%) rotate(90deg);
          transform-origin: left bottom;
        }
      }
    }

    &.\--left {
      .hidden-container__button {
        left: 100%;
        transform: translateY(-100%) rotate(90deg);
        transform-origin: left bottom;
      }
    }

    &.\--hide {
      .hidden-container__button {
        @include mq($from: tablet) {
          animation: bounce 2s infinite;
        }
      }

      @keyframes bounce {
        0% {
          margin-left: 0;
        }

        25% {
          margin-left: 3px;
        }

        50% {
          margin-left: 0;
        }

        100% {
          margin-left: 0;
        }
      }
    }
  }
</style>
